// =====================================================================
// src/components/intl-acca/DebriefMetricCard.tsx
// =====================================================================
// Single metric tile for the Post-Tournament Debrief screen.
// Designed for the executive_summary fields of DebriefEnvelope —
// every field is nullable; component MUST render the empty path
// when no settled legs exist yet (engine-validation default).

interface DebriefMetricCardProps {
  label: string;
  /** The metric value. null → render the "Awaiting data" empty path. */
  value: number | null;
  /** Format the value once non-null. */
  format?: "pp" | "pct" | "count" | "decimal";
  /** Optional subtitle / target line ("> 55% target", "n=18", etc). */
  sub?: string;
  /** Optional "pass / fail" tint vs the value's target. Defaults to neutral. */
  status?: "pos" | "neg" | "warn" | null;
}

function formatValue(value: number, format: NonNullable<DebriefMetricCardProps["format"]>): string {
  switch (format) {
    case "pp":
      return `${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(1)}pp`;
    case "pct":
      return `${Math.round(value * 100)}%`;
    case "count":
      return String(Math.round(value));
    case "decimal":
      return value.toFixed(2);
  }
}

export function DebriefMetricCard({
  label,
  value,
  format = "count",
  sub,
  status,
}: DebriefMetricCardProps): JSX.Element {
  const statusColor =
    status === "pos" ? "var(--pos)" :
    status === "neg" ? "var(--neg)" :
    status === "warn" ? "var(--warn)" :
    "var(--text)";

  return (
    <div className="ia-metric">
      <div className="ia-metric-label">{label}</div>
      {value === null ? (
        <div className="ia-metric-value-empty">Awaiting data</div>
      ) : (
        <div className="ia-metric-value" style={{ color: statusColor }}>
          {formatValue(value, format)}
        </div>
      )}
      {sub ? <div className="ia-metric-sub">{sub}</div> : null}
    </div>
  );
}
